/**
 * DV-TEMP-003: Retry Hook for Demo Viewer
 *
 * Automatic retry mechanism for transient 5xx errors with exponential backoff.
 * Integrates with React Query and provides error banner notifications.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { DemoError } from '@/lib/demo-error-handler';

interface RetryState {
  isRetrying: boolean;
  retryCount: number;
  nextRetryAt?: Date;
  maxRetries: number;
}

interface UseErrorRetryOptions {
  maxRetries?: number;
  baseDelay?: number; // milliseconds
  maxDelay?: number; // milliseconds
  onRetryAttempt?: (count: number) => void;
  onRetrySuccess?: () => void;
  onRetryFailure?: (error: DemoError) => void;
}

interface UseErrorRetryResult {
  retryState: RetryState;
  scheduleRetry: (error: DemoError, refetchFn: () => Promise<any>) => void;
  cancelRetry: () => void;
  forceRetry: (refetchFn: () => Promise<any>) => Promise<void>;
  canRetry: (error: DemoError | null) => boolean;
}

export function useErrorRetry(options: UseErrorRetryOptions = {}): UseErrorRetryResult {
  const {
    maxRetries = 3,
    baseDelay = 2000,
    maxDelay = 30000,
    onRetryAttempt,
    onRetrySuccess,
    onRetryFailure
  } = options;

  const [retryState, setRetryState] = useState<RetryState>({
    isRetrying: false,
    retryCount: 0,
    maxRetries
  });

  const timeoutRef = useRef<NodeJS.Timeout>();
  const activePromiseRef = useRef<Promise<any>>();

  // Calculate exponential backoff delay
  const calculateDelay = useCallback((retryCount: number): number => {
    const exponentialDelay = baseDelay * Math.pow(2, retryCount);
    const jitteredDelay = exponentialDelay * (0.5 + Math.random() * 0.5); // 50-100% jitter
    return Math.min(jitteredDelay, maxDelay);
  }, [baseDelay, maxDelay]);

  // Check if error is retryable
  const canRetry = useCallback((error: DemoError | null): boolean => {
    if (!error) return false;
    if (retryState.retryCount >= maxRetries) return false;
    return error.retryable;
  }, [retryState.retryCount, maxRetries]);

  // Schedule automatic retry with exponential backoff
  const scheduleRetry = useCallback((error: DemoError, refetchFn: () => Promise<any>) => {
    if (!canRetry(error)) return;

    const delay = error.canRetryIn || calculateDelay(retryState.retryCount);
    const retryAt = new Date(Date.now() + delay);

    setRetryState(prev => ({
      ...prev,
      isRetrying: true,
      nextRetryAt: retryAt
    }));

    timeoutRef.current = setTimeout(async () => {
      try {
        onRetryAttempt?.(retryState.retryCount + 1);

        const promise = refetchFn();
        activePromiseRef.current = promise;

        await promise;

        setRetryState(prev => ({
          ...prev,
          isRetrying: false,
          retryCount: 0, // Reset on success
          nextRetryAt: undefined
        }));

        onRetrySuccess?.();
      } catch (retryError: any) {
        const newRetryCount = retryState.retryCount + 1;

        setRetryState(prev => ({
          ...prev,
          isRetrying: false,
          retryCount: newRetryCount,
          nextRetryAt: undefined
        }));

        onRetryFailure?.(error);

        // Schedule another retry if we haven't exceeded max attempts
        if (newRetryCount < maxRetries && error.retryable) {
          scheduleRetry(error, refetchFn);
        }
      } finally {
        activePromiseRef.current = undefined;
      }
    }, delay);
  }, [
    retryState.retryCount,
    maxRetries,
    calculateDelay,
    canRetry,
    onRetryAttempt,
    onRetrySuccess,
    onRetryFailure
  ]);

  // Cancel pending retry
  const cancelRetry = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }

    setRetryState(prev => ({
      ...prev,
      isRetrying: false,
      nextRetryAt: undefined
    }));
  }, []);

  // Force immediate retry
  const forceRetry = useCallback(async (refetchFn: () => Promise<any>) => {
    cancelRetry();

    setRetryState(prev => ({
      ...prev,
      isRetrying: true,
      nextRetryAt: undefined
    }));

    try {
      onRetryAttempt?.(retryState.retryCount + 1);

      const promise = refetchFn();
      activePromiseRef.current = promise;

      await promise;

      setRetryState(prev => ({
        ...prev,
        isRetrying: false,
        retryCount: 0, // Reset on success
      }));

      onRetrySuccess?.();
    } catch (error: any) {
      setRetryState(prev => ({
        ...prev,
        isRetrying: false,
        retryCount: prev.retryCount + 1
      }));

      throw error; // Re-throw for caller handling
    } finally {
      activePromiseRef.current = undefined;
    }
  }, [retryState.retryCount, cancelRetry, onRetryAttempt, onRetrySuccess]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Note: We don't cancel active promises as they may be React Query promises
    };
  }, []);

  return {
    retryState,
    scheduleRetry,
    cancelRetry,
    forceRetry,
    canRetry
  };
}

// Hook to get countdown until next retry
export function useRetryCountdown(nextRetryAt?: Date): number {
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  useEffect(() => {
    if (!nextRetryAt) {
      setSecondsRemaining(0);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const diff = nextRetryAt.getTime() - now.getTime();
      const seconds = Math.max(0, Math.ceil(diff / 1000));
      setSecondsRemaining(seconds);

      if (seconds <= 0) {
        setSecondsRemaining(0);
      }
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [nextRetryAt]);

  return secondsRemaining;
}